import { useState, useEffect } from 'react'
import api from '@crm-src/lib/api'

export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  active: boolean
  created_at: string
  updated_at: string
}

interface UseUsersResult {
  users: User[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

interface UseUsersOptions {
  /** When `/users` (an admin read) answers 403, fall back to
   * `/users/assignable_users` — the same roster, id + name only — instead of an
   * empty list. For pickers a NON-admin may legitimately use, e.g. a holder of
   * `crm:resellers_salespeople` choosing who looks after a reseller. Off by
   * default so the many admin-only consumers keep their one request. */
  assignableFallback?: boolean
}

/** The assignable roster in the `User` shape. It carries no email (crm-api
 * keeps it to id + name on purpose), so `email` is empty. */
async function fetchAssignableUsers(): Promise<User[]> {
  const response = await api.get('/users/assignable_users')
  const rows: Array<{ id: string | number; full_name: string }> = response.data?.users ?? []
  return rows.map((u) => ({
    id: String(u.id),
    email: '',
    first_name: '',
    last_name: '',
    full_name: u.full_name,
    active: true,
    created_at: '',
    updated_at: '',
  }))
}

export const useUsers = ({ assignableFallback = false }: UseUsersOptions = {}): UseUsersResult => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch all active users - you may need to adjust this endpoint
      const response = await api.get('/users')
      
      // Handle both direct array and wrapped response formats
      const usersData = response.data.data || response.data.users || response.data
      
      if (Array.isArray(usersData)) {
        setUsers(usersData.filter(user => user.active !== false))
      } else {
        console.warn('Unexpected users data format:', response.data)
        setUsers([])
      }
    } catch (err: any) {
      // Handle permission errors gracefully (403 Forbidden for non-admin users)
      if (err?.response?.status === 403) {
        // Non-admin users don't have access to users endpoint
        // This is expected behavior - set empty users array and no error
        if (assignableFallback) {
          try {
            setUsers(await fetchAssignableUsers())
            setError(null)
            return
          } catch {
            /* fall through to the empty list */
          }
        }
        setUsers([])
        setError(null) // Don't show error for permission issues
        return
      }
      
      // Ignore cancelled requests
      const isCancelled = err.code === 'ERR_NETWORK_CHANGED' || 
                         err.code === 'ERR_CANCELED' ||
                         (err.message === 'Network Error' && !err.response && !err.request?.status);
      
      if (isCancelled) {
        setUsers([])
        setLoading(false)
        return
      }
      
      // Only log and show errors for actual failures
      console.error('Error fetching users:', err)
      setError(err?.response?.data?.error || err?.message || 'Failed to fetch users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  return {
    users,
    loading,
    error,
    refresh: fetchUsers
  }
}